import { useContext } from 'react';
import { keyBy, sortBy, find, isFunction } from 'lodash';
import { useTranslation } from 'react-i18next';

import UIContext from 'components/Utils/UIContext';

import { Button, Flex, Tag, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faClone, faClose, faEdit, faEyeSlash, faLink, faTrash } from '@fortawesome/pro-light-svg-icons';

import ActionButton from 'components/action/ActionButton';

import { itemActiveness } from 'utils/ranking';

import Weight from 'components/controls/Weight';
import { getScoreColor } from 'utils/ranking';
import { isItemInactive, isItemNeutral, getItemAlteredByScenario } from 'utils/rules';
import RisksTab from './RisksTab';
import TagsTab from './TagsTab';
import { FishboneLinksReadonly } from 'components/tabs/FishboneLinksReadonly';
import { useStakeholderController } from 'hooks/useStakeholderController';

import './GeneralTab.sass';

// Props
interface GeneralTabProps {
	selection: any;
	stakes: any;
	indicators: any;
	onEdit: any;
	onClone: any;
	onDisable: any;
	onDelete: any;
	scenario: any;
	displayIndicators: boolean;
	displayStakes: boolean;
	onUpdateStakeholderTags: Function;
	onCreateTag: Function;
	tags: any;

	setPreferences: Function;
	areIndicatorsVisible: boolean;
	areStakesVisible: boolean;
	areRisksVisible: boolean;
	areOpportunitiesVisible: boolean;

	fishboneBranchs?: any[];

}

export const GeneralTab = (props: GeneralTabProps) => {
	const { t } = useTranslation();
	const uiContext = useContext(UIContext);

	const { displayTabs = true, displayTitle = false } = props;

	// Filtrer les éléments par enabled !== false après application du scénario
	const filteredStakes = (props.stakes || [])
		.map((i: any) => getItemAlteredByScenario(i, props.scenario))
		.filter((i: any) => i.enabled !== false);

	const filteredIndicators = (props.indicators || [])
		.map((c: any) => getItemAlteredByScenario(c, props.scenario))
		.filter((c: any) => c.enabled !== false);

	const indexedStakes = keyBy(props.stakes, 'id');

	const isImpactingStake = (stakeId: string) => {
		if (
			indexedStakes[stakeId] &&
			props.selection?.impactedStakes
				?.map((ii: any) => ii.stakeId)
				.includes(stakeId)
		)
			return true;
		return false;
	};

	const getImpactingStakeDetails = (stakeId: string) => {
		return find(
			props.selection?.impactedStakes || [],
			(ii: any) => ii.stakeId == stakeId
		);
	};

	// Stakeholder impacts
	const { createStakeImpact, updateStakeImpactStrength, deleteStakeImpact, createIndicatorImpact, updateIndicatorImpactStrength, deleteIndicatorImpact } = useStakeholderController();

	const indexedIndicators = keyBy(props.indicators, 'id');

	const isImpactingIndicator = (indicatorId: string) => {
		if (
			indexedIndicators[indicatorId] &&
			props.selection?.impactedIndicators
				?.map((ii: any) => ii.indicatorId)
				.includes(indicatorId)
		)
			return true;
		return false;
	};

	const getImpactingIndicatorDetails = (indicatorId: string) => {
		return find(
			props.selection?.impactedIndicators || [],
			(ii: any) => ii.indicatorId == indicatorId
		);
	};

	return (
		<>
			<Flex vertical className="GeneralTab noselection">

				{displayTitle && <>
					<Flex className="mb-1">
						<div className="w-100 text-left">
							<div className="title">{props.selection?.name}</div>
							<small>{t("models.item.name")}</small>
						</div>
						<Tooltip title={t('actions.close')} placement='left'>
							<Button
								shape="circle"

								variant="solid"
								color="primary"
								className="mini ml-auto"

								tooltip={t('models.item.actions.edit')}
								style={{ marginRight: '-0.9rem' }}
								onClick={props.onClose}
							>
								<FontAwesomeIcon icon={faClose} />
							</Button></Tooltip>
					</Flex>

					<ul>
						{props.selection?.description && (
							<li className="EditableListItem description">
								<div>{props.selection?.description}</div>
							</li>
						)}
					</ul>
				</>}


				<div className="text-left mt-05">
					{(props.selection?.tags || []).map((t, index) => (
						<Tag
							color={'#363D4D'}
							style={{ color: '#A6B9C8' }}
							className="tag"
						>
							{t}
						</Tag>
					))}
				</div>

				<ul>
					<li className="EditableListItem">
						<div style={{ width: '45%', textAlign: 'left' }}>
							{t('models.item.fields.trend.label')}
						</div>
						<div>
							{props.selection?.trend
								? {
									green: t('models.item.fields.trend.level.green'),
									orange: t('models.item.fields.trend.level.orange'),
									neutral: t('models.item.fields.trend.level.neutral'),
									red: t('models.item.fields.trend.level.red')
								}[props.selection?.trend]
								: 'Neutral'}
						</div>
					</li>

					<li className="EditableListItem">
						<div style={{ width: '45%', textAlign: 'left' }}>
							{t('models.item.engagement')}
						</div>
						<div>
							{isItemNeutral(props.selection) ? 'Neutral' :
								props.selection?.impact
									? {
										low: 'Low',
										medium: 'Medium',
										high: 'High',
										undefined: 'Inactive / Neutral'
									}[
									itemActiveness(
										props.selection?.x,
										props.selection?.y
									)
									]
									: null}{' '}
							{!props.selection || isItemInactive(props.selection) || isItemNeutral(props.selection)
								? ''
								: props.selection?.y > 0
									? 'positive'
									: 'negative'}
						</div>
					</li>

					<li className="EditableListItem">
						<div style={{ width: '45%', textAlign: 'left' }}>
							{t('models.item.fields.impact.label')}
						</div>
						<div>
							{props.selection?.impact
								? {
									low: t('models.item.fields.impact.level.low'),
									medium: t('models.item.fields.impact.level.medium'),
									high: t('models.item.fields.impact.level.high')
								}[props.selection.impact]
								: 'Low'}
						</div>
					</li>
					{props.selection?.description && (
						<li className="EditableListItem description pt-1">
							<div>{props.selection?.description}</div>
						</li>
					)}
				</ul>

				{uiContext.canEdit && <div className="Actions mt-1 mb-05 text-center ml-auto mr-auto">
					{isFunction(props.onEdit) && <ActionButton
						className="editStakeholder small ml-05"
						tooltip={t('models.item.actions.edit')}
						onClick={() => props.onEdit(props.selection.id)}
					>
						<FontAwesomeIcon icon={faEdit} />
					</ActionButton>}

					{!props.scenario && isFunction(props.onCreateLink) && <ActionButton
						className="createStakeholderLink small"
						tooltip={t('models.item_link.actions.create_new')}
						onClick={props.onCreateLink}
					>
						<FontAwesomeIcon icon={faLink} />
					</ActionButton>}

					{isFunction(props.onDisable) && <ActionButton
						className="disableStakeholder small"
						tooltip={t('actions.disable')}
						onClick={() => props.onDisable(props.selection.id)}
					>
						<FontAwesomeIcon icon={faEyeSlash} />
					</ActionButton>}

					{!props.scenario && isFunction(props.onDelete) && <ActionButton
						className="deleteStakeholder small"
						tooltip={t('models.item.actions.delete')}
						onClick={() => props.onDelete(props.selection.id)}
					>
						<FontAwesomeIcon icon={faTrash} />
					</ActionButton>
					}
				</div>}

				{displayTabs && <TagsTab
					key={
						props.selection?.id
					}
					{...props}
					onUpdateStakeholderTags={props.onUpdateStakeholderTags}
					selection={props.selection}
					allTags={props.tags}
					onCreateTag={props.onCreateTag}
					scenario={props.scenario}
				/>}

<FishboneLinksReadonly
						title={t('pages.fishbone.title')}
						links={props.selection?.fishboneLinks}
						fishboneBranchs={props.fishboneBranchs}
					/>

				{props.displayStakes && (
					<Flex vertical className="mt-2">
						<Flex className="text-left mb-05">
							<strong>{t('models.item.fields.stakes.name')}</strong>

							{isFunction(props.setPreferences) && <Button shape="circle" className="mini ml-auto" onDoubleClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}} onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								props.setPreferences(
									'pages',
									'navigator.stakes',
									!props.areStakesVisible
								);
							}}>
								<FontAwesomeIcon
									icon={!props.areStakesVisible ? faChevronDown : faChevronUp}
								></FontAwesomeIcon>
							</Button>}

						</Flex>
					{(!isFunction(props.setPreferences) || props.areStakesVisible) && <div className="text-left">
						{filteredStakes && filteredStakes.length ? (
							sortBy(filteredStakes || [], 'name').map(
									(i: any, index) => {
										const impactedStake =
											getImpactingStakeDetails(i.id);

										return (
											<Tag.CheckableTag
												checked={isImpactingStake(i.id)}
												key={index}
											onClick={() => {
												if (!uiContext.canEdit) return;
												console.log(
													'impactedStake',
													impactedStake
												);

												if (
													!impactedStake ||
													!impactedStake.id
												) {
													createStakeImpact(
														props.selection?.id,
														i.id
													);
												} else if (
													impactedStake.strength <
													3
												) {
													updateStakeImpactStrength(
														impactedStake,
														impactedStake.strength +
														1
													);
												} else {
													deleteStakeImpact(
														impactedStake.id,
														props.selection?.id
													);
												}
											}}
												className={[
													'ImpactedStake tag nowrap'
												].join(' ')}
											>
												<div
													className={
														'Trend v' + i.trend
													}
													style={
														i.trend == '50'
															? {}
															: {
																background:
																	getScoreColor(
																		i.trend
																	)
															}
													}
												></div>
												<span className="tag__label" title={i.name}>{i.name}</span>

												{impactedStake ? (
													<Weight onClick={() => { }}>
														{
															impactedStake.strength
														}
													</Weight>
												) : null}
											</Tag.CheckableTag>
										);
									}
								)
							) : (
								<div
									className="text-center"
									style={{ color: 'var(--ant-color-text-tertiary)' }}
								>
									{t('models.stake.no_x')}
								</div>
							)}
						</div>}
					</Flex>
				)}

				<RisksTab
					key={
						props.selection?.id +
						'-' +
						props.selection?.impactedRisks
							.length
					}
					{...props}
				></RisksTab>

				{props.displayIndicators && (
					<Flex vertical className="mt-2">
						<Flex className="text-left mb-05">
							<strong>{t('models.item.fields.indicators.name')}</strong>

							<Button shape="circle" className="mini ml-auto" onDoubleClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}} onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								props.setPreferences(
									'pages',
									'navigator.indicators',
									!props.areIndicatorsVisible
								);
							}}>
								<FontAwesomeIcon
									icon={!props.areIndicatorsVisible ? faChevronDown : faChevronUp}
								></FontAwesomeIcon>
							</Button>

						</Flex>
					{props.areIndicatorsVisible && <div className="text-left">
						{filteredIndicators && filteredIndicators.length ? (
							sortBy(filteredIndicators || [], 'name').map(
									(i: any) => {
										const impactedIndicator =
											getImpactingIndicatorDetails(i.id);

										return (
											<Tag.CheckableTag
												key={i.id}
											onClick={() => {
												console.log(
													'impactedIndicator',
													impactedIndicator
												);

												if (
													!impactedIndicator ||
													!impactedIndicator.id
												) {
													createIndicatorImpact(
														props.selection?.id,
														i.id
													);
												} else if (
													impactedIndicator.strength <
													3
												) {
													updateIndicatorImpactStrength(
														impactedIndicator,
														impactedIndicator.strength +
														1
													);
												} else {
													deleteIndicatorImpact(
														impactedIndicator.id,
														props.selection?.id
													);
												}
											}}
												checked={isImpactingIndicator(i.id)}
												className={[
													'ImpactedIndicator tag nowrap'
												].join(" ")}
											>
												<div
													className={
														'Trend v' + i.trend
													}
													style={
														i.trend == '50'
															? {}
															: {
																background:
																	getScoreColor(
																		i.trend
																	)
															}
													}
												></div>
												<span className="tag__label" title={i.name}>{i.name}</span>

												{impactedIndicator ? (
													<Weight onClick={() => { }}>
														{
															impactedIndicator.strength
														}
													</Weight>
												) : null}
											</Tag.CheckableTag>
										);
									}
								)
							) : (
								<div
									className="text-center"
									style={{ color: 'var(--ant-color-text-tertiary)' }}
								>
									{t('models.indicator.no_x')}
								</div>
							)}
						</div>}
					</Flex>
				)}
			</Flex>
		</>
	);
};

export default GeneralTab;
